import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import API_URLS from "../../../../constants/apiConstants";
import { RecordDetailDialog } from "./RecordDetailDialog";
import styles from "../../css/IndividualReport.module.css";
import { QuranReportTable } from "../../../../components/components/js/QuranReportTable";

// ─── Color tokens  ( yellow=memorization, pink=revision) ──────────────
const YELLOW = {
  bg:        "#FDFAF0",
  border:    "#EDD98A",
  text:      "#5C4500",
  accent:    "#A87D00",
  hdrBg:     "#FBF3D0",
  thBg:      "#FBF3D0",
  statNum:   "#5C4500",
  pagActive: "#A87D00",
};
const PINK = {
  bg:        "#FDF0F4",
  border:    "#F2C8D6",
  text:      "#6B1E38",
  accent:    "#C94B74",
  hdrBg:     "#FAE4EC",
  thBg:      "#FAE4EC",
  statNum:   "#6B1E38",
  pagActive: "#C94B74",
};

// MEM = yellow, REV = pink
const MEM = YELLOW;
const REV = PINK;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const buildRefByLang = (rec, lang, t) => {
  const juz    = rec.juz ?? rec.juz_revision;
  const juzMul = rec.juz_mul?.length ? rec.juz_mul : rec.juz_mul_revision ?? [];

  const isTr = lang === "tr";
  const isAr = lang === "ar";

  const juzLabel  = t("admin_pages.quran_report.juz_label");
  const separator   = isAr ? "، " : ", ";

  const mulKey      = isAr ? "sura_mul_ar"  : isTr ? "sura_mul_tr"  : "sura_mul";
  const fromKey     = isAr ? "sura_from_ar" : isTr ? "sura_from_tr" : "sura_from";
  const toKey       = isAr ? "sura_to_ar"   : isTr ? "sura_to_tr"   : "sura_to";
  const dot         = isAr ? " · " : " · ";

  const parts = [];

  if (juz) parts.push(`${juzLabel} ${juz}`);
  if (juzMul?.length) parts.push(juzMul.map(j => `${juzLabel} ${j}`).join(separator));

  const mul = rec[mulKey];
  if (mul?.length) {
    parts.push(mul.filter(Boolean).join(separator));
  } else {
    const from = rec[fromKey];
    const to   = rec[toKey];
    const ayaF = rec.aya_from;
    const ayaT = rec.aya_to;
    if (from) {
      if (to && to !== from) {
        parts.push(`${ayaF ? `${from} ${ayaF}` : from} – ${ayaT ? `${to} ${ayaT}` : to}`);
      } else if (ayaF && ayaT) {
        parts.push(`${from} ${ayaF}–${ayaT}`);
      } else {
        parts.push(from);
      }
    }
  }

  return parts.length ? parts.join(dot) : (isAr ? null : "—");
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toISOString().slice(0, 10);
};

const cx = (...cls) => cls.filter(Boolean).join(" ");

// ─── Column definitions ───────────────────────────────────────────────────────
// label keys — resolved in render via t()
const REC_COLUMNS = (t) => [
  { key: "activity_date",    label: t("admin_pages.quran_report.table_cols.date"),             center: false },
  { key: "ref",              label: t("admin_pages.quran_report.table_cols.reference"),         center: false, ref: true },
  { key: "score",            label: t("admin_pages.quran_report.table_cols.score"),             mono: true },
  { key: "mistakes_telawa",  label: t("admin_pages.quran_report.table_cols.mistakes_tilawa"),   mono: true, wrap: true },
  { key: "mistakes_tagweed", label: t("admin_pages.quran_report.table_cols.mistakes_tajweed"),  mono: true, wrap: true },
  { key: "note",             label: t("admin_pages.quran_report.table_cols.notes"),             center: false, note: true },
];

const HW_COLUMNS = (t) => [
  { key: "activity_date", label: t("admin_pages.quran_report.table_cols.date"),     center: false },
  { key: "ref",           label: t("admin_pages.quran_report.table_cols.reference"), center: false, ref: true },
  { key: "note",          label: t("admin_pages.quran_report.table_cols.notes"),     center: false, note: true },
];

const flattenRec = (r, lang, t) => ({
  ...r,
  activity_date:    fmtDate(r.activity_date),
  ref:  buildRefByLang(r, lang, t) ?? "—",
  score:            r.score            ?? "—",
  mistakes_telawa:  r.mistakes_telawa  ?? 0,
  mistakes_tagweed: r.mistakes_tagweed ?? 0,
  note:             r.note             || "—",
});

const flattenHw = (r, lang, t) => ({
  ...r,
  activity_date: fmtDate(r.activity_date),
  ref:  buildRefByLang(r, lang, t) ?? "—",
  note:          r.note || "—",
});

// ─── Paginated state factory ──────────────────────────────────────────────────
const initPaged = () => ({
  results: [], total_items: 0, total_pages: 0, current_page: 1, loading: false,
});

// ─── Main component ───────────────────────────────────────────────────────────
export const IndividualReport = ({ reportRef }) => {
  const { t, i18n } = useTranslation();
  const { school }   = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const lang = i18n.language;

  // ── Filter state ───────────────────────────────────────────────────────────
  const [rooms,           setRooms]           = useState([]);
  const [students,        setStudents]        = useState([]);
  const [selectedRoom,    setSelectedRoom]    = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [startDate,       setStartDate]       = useState("");
  const [endDate,         setEndDate]         = useState("");
  const [errors,          setErrors]          = useState({});
  const [loadingRooms,    setLoadingRooms]    = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSummary,  setLoadingSummary]  = useState(false);

  // ── View toggle ────────────────────────────────────────────────────────────
  const [view, setView] = useState("all");

  // ── Data ───────────────────────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [recMem,  setRecMem]  = useState(initPaged());
  const [recRev,  setRecRev]  = useState(initPaged());
  const [hwMem,   setHwMem]   = useState(initPaged());
  const [hwRev,   setHwRev]   = useState(initPaged());

  const [activeParams, setActiveParams] = useState(null);

  // ── Dialog state ───────────────────────────────────────────────────────────
  const [dialog, setDialog] = useState(null);
  const openDialog  = (record, type, tokens) => setDialog({ record, type, tokens });
  const closeDialog = () => setDialog(null);

  const showMem  = view === "all" || view === "mem";
  const showRev  = view === "all" || view === "rev";
  const showBoth = showMem && showRev;

  // ── Load rooms ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingRooms(true);
        const res = await axiosPrivate.get(API_URLS.getSchoolRooms(school.id));
        setRooms(res.data?.map(r => ({ value: r.id, label: r.ClassName })) || []);
      } catch (_) {}
      finally { setLoadingRooms(false); }
    })();
  }, [axiosPrivate, school.id]);

  // ── Load students when room changes ───────────────────────────────────────
  useEffect(() => {
    if (!selectedRoom) { setStudents([]); setSelectedStudent(""); return; }
    (async () => {
      try {
        setLoadingStudents(true);
        const res = await axiosPrivate.get(API_URLS.getRoomStudents(selectedRoom));
        setStudents(res.data?.map(s => ({ value: s.id, label: s.name })) || []);
      } catch (_) {}
      finally { setLoadingStudents(false); }
    })();
  }, [axiosPrivate, selectedRoom]);

  // ── Generic paginated fetch ────────────────────────────────────────────────
  const fetchRecords = useCallback(async (url, page, params, setter) => {
    setter(prev => ({ ...prev, loading: true }));
    try {
      const qs = new URLSearchParams({
        student_id: params.student_id,
        start_date: params.start_date,
        end_date:   params.end_date,
        page,
        page_size:  8,
      });
      const res = await axiosPrivate.get(`${url}?${qs}`);
      const d   = res.data;
      setter({
        results:      d.results      ?? [],
        total_items:  d.total_items  ?? 0,
        total_pages:  d.total_pages  ?? 1,
        current_page: d.current_page ?? page,
        loading:      false,
      });
    } catch (_) {
      setter(prev => ({ ...prev, loading: false }));
    }
  }, [axiosPrivate]);

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {
      room:      !selectedRoom,
      student:   !selectedStudent,
      startDate: !startDate,
      endDate:   !endDate,
    };
    setErrors(e);
    return !Object.values(e).some(Boolean);
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const handleFilter = async () => {
    if (!validate()) return;

    const params = {
      student_id: selectedStudent,
      start_date: startDate,
      end_date:   endDate,
    };
    setActiveParams(params);

    try {
      setLoadingSummary(true);
      const res = await axiosPrivate.get(
        `${API_URLS.quranReportSummary}?${new URLSearchParams(params)}`
      );
      setSummary(res.data);
    } catch (_) { setSummary(null); }
    finally { setLoadingSummary(false); }

    fetchRecords(API_URLS.quranRecitationMemorization, 1, params, setRecMem);
    fetchRecords(API_URLS.quranRecitationRevision,     1, params, setRecRev);
    fetchRecords(API_URLS.quranHomeworkMemorization,   1, params, setHwMem);
    fetchRecords(API_URLS.quranHomeworkRevision,       1, params, setHwRev);
  };

  // ── Pagination callbacks ───────────────────────────────────────────────────
  const onMemPage   = (p) => fetchRecords(API_URLS.quranRecitationMemorization, p, activeParams, setRecMem);
  const onRevPage   = (p) => fetchRecords(API_URLS.quranRecitationRevision,     p, activeParams, setRecRev);
  const onHwMemPage = (p) => fetchRecords(API_URLS.quranHomeworkMemorization,   p, activeParams, setHwMem);
  const onHwRevPage = (p) => fetchRecords(API_URLS.quranHomeworkRevision,       p, activeParams, setHwRev);

  const roomLabel    = rooms.find(r    => r.value === Number(selectedRoom))?.label    || "—";
  const studentLabel = students.find(s => s.value === Number(selectedStudent))?.label || "—";
  const hasData      = summary !== null;

  // ── Current position display (Arabic-aware) ────────────────────────────────
  const memCurrentDisplay = 
    lang === "ar"? (summary?.current_memorization_ar || summary?.current_memorization_sura || "—")
   : lang === "tr" ? (summary?.current_memorization_tr || summary?.current_memorization || "—")
      : (summary?.current_memorization || "—");

  const revCurrentDisplay = 
  lang === "ar"? (summary?.current_revision_ar || summary?.current_revision_sura || "—")
    : lang === "tr" ? (summary?.current_revision_tr || summary?.current_revision || "—")
      : (summary?.current_revision|| "—");

  const recMemColumns = REC_COLUMNS(t);
  const recHwColumns  = HW_COLUMNS(t);

  return (
    <div className={styles.reportRoot}>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div className={styles.filtersContainer}>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("admin_pages.quran_report.filters.room")}
          </label>
          <select
            className={styles.filterSelect}
            value={selectedRoom}
            onChange={e => setSelectedRoom(e.target.value)}
            disabled={loadingRooms}
          >
            <option value="">{t("admin_pages.quran_report.filters.select_room")}</option>
            {rooms.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {errors.room && (
            <span className={styles.filterError}>
              {t("admin_pages.quran_report.filters.required")}
            </span>
          )}
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("admin_pages.quran_report.filters.student")}
          </label>
          <select
            className={styles.filterSelect}
            value={selectedStudent}
            onChange={e => setSelectedStudent(e.target.value)}
            disabled={!selectedRoom || loadingStudents}
          >
            <option value="">
              {!selectedRoom
                ? t("admin_pages.quran_report.filters.select_room_first")
                : t("admin_pages.quran_report.filters.select_student")}
            </option>
            {students.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {errors.student && (
            <span className={styles.filterError}>
              {t("admin_pages.quran_report.filters.required")}
            </span>
          )}
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("admin_pages.quran_report.filters.start_date")}
          </label>
          <input
            type="date"
            className={styles.filterInput}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          {errors.startDate && (
            <span className={styles.filterError}>
              {t("admin_pages.quran_report.filters.required")}
            </span>
          )}
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("admin_pages.quran_report.filters.end_date")}
          </label>
          <input
            type="date"
            className={styles.filterInput}
            value={endDate}
            min={startDate}
            onChange={e => setEndDate(e.target.value)}
          />
          {errors.endDate && (
            <span className={styles.filterError}>
              {t("admin_pages.quran_report.filters.required")}
            </span>
          )}
        </div>

        <button
          className={cx(styles.filterBtn, styles.filterBtnPrimary)}
          onClick={handleFilter}
          disabled={loadingSummary}
        >
          {loadingSummary
            ? t("admin_pages.quran_report.filters.filtering")
            : t("admin_pages.quran_report.filters.filter")}
        </button>

      </div>

      {/* ── Report body ──────────────────────────────────────────────────── */}
      {hasData && (
        <div ref={reportRef}>

          {/* View toggle */}
          <div className={styles.toggleBar}>
            <span className={styles.toggleBarLabel}>
              {t("admin_pages.quran_report.toggle.show")}
            </span>
            <button
              className={cx(styles.toggleBtn, view === "all" && styles.toggleBtnAll)}
              onClick={() => setView("all")}
            >
              {t("admin_pages.quran_report.toggle.all")}
            </button>
            <button
              className={cx(styles.toggleBtn, view === "mem" && styles.toggleBtnMem)}
              onClick={() => setView("mem")}
            >
              📖 {t("admin_pages.quran_report.toggle.memorization")}
            </button>
            <button
              className={cx(styles.toggleBtn, view === "rev" && styles.toggleBtnRev)}
              onClick={() => setView("rev")}
            >
              🔁 {t("admin_pages.quran_report.toggle.revision")}
            </button>
          </div>

          {/* Header banner — "This report was prepared for [name] for the period from [date] to [date]" */}
          <div className={styles.banner}>
            <p className={styles.bannerTitle}>
              {t("admin_pages.quran_report.banner.report_prepared_for")}{" "}
              <strong>{studentLabel}</strong>{" "}
              {t("admin_pages.quran_report.banner.for_period_from")}{" "}
              <strong>{startDate}</strong>{" "}
              {t("admin_pages.quran_report.banner.to")}{" "}
              <strong>{endDate}</strong>
            </p>
            <div className={styles.bannerMeta}>
              <span className={styles.bannerChip}>
                🏠 {t("admin_pages.quran_report.filters.room")}: {roomLabel}
              </span>
              <span className={styles.bannerChip}>
                🎂 {t("admin_pages.quran_report.banner.age")}: {summary.age ?? "—"}
              </span>
            </div>
          </div>

          {/* ── Progress panels ─────────────────────────────────────────── */}
          <div className={cx(styles.panelsGrid, !showBoth && styles.singleCol)}>

            {/* Memorization = YELLOW */}
            {showMem && (
              <div className={styles.panel}>
                <div
                  className={styles.panelHeader}
                  style={{ background: MEM.hdrBg, color: MEM.text, borderBottom: `1px solid ${MEM.border}` }}
                >
                  <span>📖</span>
                  {t("admin_pages.quran_report.sections.memorization")}
                </div>
                <div className={styles.panelBody}>
                  <div className={styles.sectionLabel} style={{ color: MEM.accent }}>
                    {t("admin_pages.quran_report.sections.recent_stop")}
                  </div>
                  <div className={styles.posBlock} style={{ background: MEM.bg, border: `1px solid ${MEM.border}` }}>
                    <span className={styles.posKey} style={{ color: MEM.accent }}>
                      {t("admin_pages.quran_report.sections.last_recitation")}
                    </span>
                    <span className={styles.posVal}>
                      {memCurrentDisplay}
                    </span>
                  </div>
                  <div className={styles.sectionLabel} style={{ color: MEM.accent }}>
                    {t("admin_pages.quran_report.sections.progress")}
                  </div>
                  <div className={styles.statsGrid}>
                    <div className={styles.statBox} style={{ background: MEM.bg, border: `1px solid ${MEM.border}` }}>
                      <div className={styles.statNumber} style={{ color: MEM.statNum }}>{summary.memorization_sura ?? 0}</div>
                      <div className={styles.statLabel}>{t("admin_pages.quran_report.sections.suras")}</div>
                    </div>
                    <div className={styles.statBox} style={{ background: MEM.bg, border: `1px solid ${MEM.border}` }}>
                      <div className={styles.statNumber} style={{ color: MEM.statNum }}>{summary.memorization_ayat ?? 0}</div>
                      <div className={styles.statLabel}>{t("admin_pages.quran_report.sections.ayahs")}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Revision = PINK */}
            {showRev && (
              <div className={styles.panel}>
                <div
                  className={styles.panelHeader}
                  style={{ background: REV.hdrBg, color: REV.text, borderBottom: `1px solid ${REV.border}` }}
                >
                  <span>🔁</span>
                  {t("admin_pages.quran_report.sections.revision")}
                </div>
                <div className={styles.panelBody}>
                  <div className={styles.sectionLabel} style={{ color: REV.accent }}>
                    {t("admin_pages.quran_report.sections.recent_stop")}
                  </div>
                  <div className={styles.posBlock} style={{ background: REV.bg, border: `1px solid ${REV.border}`}}>
                    <span className={styles.posKey} style={{ color: REV.accent }}>
                      {t("admin_pages.quran_report.sections.last_recitation")}
                    </span>
                    <span className={styles.posVal}>
                      {revCurrentDisplay}
                    </span>
                  </div>
                  <div className={styles.sectionLabel} style={{ color: REV.accent }}>
                    {t("admin_pages.quran_report.sections.progress")}
                  </div>
                  <div className={styles.statsGrid}>
                    <div className={styles.statBox} style={{ background: REV.bg, border: `1px solid ${REV.border}` }}>
                      <div className={styles.statNumber} style={{ color: REV.statNum }}>{summary.revision_sura ?? 0}</div>
                      <div className={styles.statLabel}>{t("admin_pages.quran_report.sections.suras")}</div>
                    </div>
                    <div className={styles.statBox} style={{ background: REV.bg, border: `1px solid ${REV.border}` }}>
                      <div className={styles.statNumber} style={{ color: REV.statNum }}>{summary.revision_ayat ?? 0}</div>
                      <div className={styles.statLabel}>{t("admin_pages.quran_report.sections.ayahs")}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Recitation Records ──────────────────────────────────────── */}
          <div className={styles.recordsSection}>
            <div className={styles.recordsHeader}>
              🎙 {t("admin_pages.quran_report.sections.recitation_records")}
            </div>
            <div className={cx(styles.recordsBody, !showBoth && styles.singleCol)}>
              {showMem && (
                <div className={cx(styles.recordsCol, showBoth && styles.withBorder)}>
                  <div className={styles.sectionLabel} style={{ color: MEM.accent }}>
                    📖 {t("admin_pages.quran_report.toggle.memorization")}
                  </div>
                  <QuranReportTable
                    columns={recMemColumns}
                    data={recMem.results.map(r => flattenRec(r, lang, t))}
                    tokens={MEM}
                    totalItems={recMem.total_items}
                    totalPages={recMem.total_pages}
                    currentPage={recMem.current_page}
                    onPage={onMemPage}
                    onRowClick={(row) => openDialog(row, "recitation", MEM)}
                    tableColsPrefix="admin_pages.quran_report.types"
                  />
                </div>
              )}
              {showRev && (
                <div className={styles.recordsCol}>
                  <div className={styles.sectionLabel} style={{ color: REV.accent }}>
                    🔁 {t("admin_pages.quran_report.toggle.revision")}
                  </div>
                  <QuranReportTable
                    columns={recMemColumns}
                    data={recRev.results.map(r => flattenRec(r, lang, t))}
                    tokens={REV}
                    totalItems={recRev.total_items}
                    totalPages={recRev.total_pages}
                    currentPage={recRev.current_page}
                    onPage={onRevPage}
                    onRowClick={(row) => openDialog(row, "recitation", REV)}
                    tableColsPrefix="admin_pages.quran_report.types"
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── Homework Records ────────────────────────────────────────── */}
          <div className={styles.recordsSection}>
            <div className={styles.recordsHeader}>
              📝 {t("admin_pages.quran_report.sections.homework_records")}
            </div>
            <div className={cx(styles.recordsBody, !showBoth && styles.singleCol)}>
              {showMem && (
                <div className={cx(styles.recordsCol, showBoth && styles.withBorder)}>
                  <div className={styles.sectionLabel} style={{ color: MEM.accent }}>
                    📖 {t("admin_pages.quran_report.toggle.memorization")}
                  </div>
                  <QuranReportTable
                    columns={recHwColumns}
                    data={hwMem.results.map(r => flattenHw(r, lang, t))}
                    tokens={MEM}
                    totalItems={hwMem.total_items}
                    totalPages={hwMem.total_pages}
                    currentPage={hwMem.current_page}
                    onPage={onHwMemPage}
                    onRowClick={(row) => openDialog(row, "homework", MEM)}
                    tableColsPrefix="admin_pages.quran_report.types"
                  />
                </div>
              )}
              {showRev && (
                <div className={styles.recordsCol}>
                  <div className={styles.sectionLabel} style={{ color: REV.accent }}>
                    🔁 {t("admin_pages.quran_report.toggle.revision")}
                  </div>
                  <QuranReportTable
                    columns={recHwColumns}
                    data={hwRev.results.map(r => flattenHw(r, lang, t))}
                    tokens={REV}
                    totalItems={hwRev.total_items}
                    totalPages={hwRev.total_pages}
                    currentPage={hwRev.current_page}
                    onPage={onHwRevPage}
                    onRowClick={(row) => openDialog(row, "homework", REV)}
                    tableColsPrefix="admin_pages.quran_report.types"
                  />
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* ── Record detail dialog ──────────────────────────────────────────── */}
      <RecordDetailDialog
        record={dialog?.record ?? null}
        type={dialog?.type}
        tokens={dialog?.tokens}
        onClose={closeDialog}
      />

    </div>
  );
};